"use client";

import React, { useEffect, useRef, useState } from 'react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const operationalFunctions = [{
  title: "Առաքելության պլանավորում",
  description: "արբանյակի նկարահանման աշխատանքների ժամանակացույցի և առաջադրանքների կազմավորում:"
}, {
  title: "Տեխնիկական վիճակի և կարգավիճակի մոնիտորինգ",
  description: "տելեմետրիկ տվյալների միջոցով արբանյակի վիճակի շարունակական վերահսկում և վերլուծություն:"
}, {
  title: "Տվյալների ընդունում և վավերացում",
  description: "ստացված պատկերների ընդունում, որակի ապահովում և կիրառելիության ստուգում:"
}, {
  title: "Վերգետնյա սեգմենտի աշխատանքներ",
  description: "վերգետնյա կայանների ամրագրում,\xA0հրամանների վերբեռնում, տվյալների ներբեռնում և ընդհանուր կապի ապահովում վերգետնյին կայանների ենթակառուցվածքի միջոցով:"
}];
export default function Operations() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="operations" className="bg-background py-16 md:py-24 overflow-hidden dark">
            <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    
                    {}
                    <div ref={leftRef} className={`flex flex-col justify-center transition-all duration-1000 ease-out ${isLeftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-6 leading-tight">
                            Արբանյակի Կառավարման Կենտրոն
                        </h2>
                        
                        <p className="text-muted-foreground mb-6 leading-relaxed !text-2xl !w-[1500px] !font-semibold">Արմսաթ 1 արբանյակի կառավարումն իրականացվում է «Գեոկոսմոս» ՓԲԸ հատուկ Արբանյակային Կառավարման Կենտրոնից:</p>
                        
                        <p className="text-foreground mb-8 !text-2xl !font-semibold">
                            Կենտրոնի հիմնական գործառույթներն են.
                        </p>
                        
                        <div className="space-y-8">
                            {operationalFunctions.map((item, i) => <div key={i} data-index={i} className="relative pl-6 border-l-2 border-primary/30 hover:border-primary transition-colors duration-300">
                                    <h3 className="font-default font-semibold text-foreground mb-2 !text-2xl">
                                        {item.title}
                                    </h3>
                                    <p className="text-muted-foreground leading-relaxed !text-xl">
                                        {item.description}
                                    </p>
                                </div>)}
                        </div>
                    </div>

                    {}
                    <div ref={rightRef} className={`relative transition-all duration-1000 delay-300 ease-out ${isRightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-muted/20 border border-border/50 p-2">
                            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666312/img/satellite_control_center_illustration.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666312/img/satellite_control_center_illustration.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1666312/img/satellite_control_center_illustration.png-wvc-sizes" alt="Արբանյակի Կառավարման Կենտրոնի Իլյուստրացիա" className="w-full object-cover rounded-xl !h-[400px]" loading="lazy" src="https://summary-monarch.10web.cloud/wp-content/uploads/2026/05/room-scaled.jpg" />
                            {}
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-lg blur-2xl -z-10"></div>
                            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary/10 rounded-lg blur-2xl -z-10"></div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}